<template>
  <v-navigation-drawer
    value="true"
    permanent
    absolute
    right
    stateless
    class="right-sidebar fill-height"
    disableOverlay
    color="#1A1A1A"
    v-if="floor_id"
    :width="drawerWidth"
  >
    <template v-slot:prepend>
      <sidebar-toolbar
        :title="
          addingRoom
            ? $t('room.add')
            : updatingRoom
            ? $t('room.update')
            : name
            ? name
            : $t('room.title')
        "
        @onBackButtonClick="closingForm = true"
        borderBottom
      ></sidebar-toolbar>

      <v-tabs
        v-if="addingRoom || updatingRoom"
        :value="tab"
        @change="(value) => setTab(value)"
        centered
        show-arrows
        background-color="#4C4C4C"
        class="pa-0 ma-0"
      >
        <v-tab class="sidebar-tab">{{ $t("room.tabs.info") }}</v-tab>
        <v-tab :disabled="!closed" class="sidebar-tab">
          {{ $t("room.tabs.grid") }}
        </v-tab>
        <v-tab class="sidebar-tab">{{ $t("room.tabs.tests") }}</v-tab>
      </v-tabs>
    </template>

    <!--tester view-->
    <v-container class="pa-0" v-if="state === 'in_testing'">
      <tester-rooms-view class="pa-6 pt-0" v-show="!room_id" />
      <tester-test-view
        v-if="room_id && !selected_measurement_point_id"
        class="pa-6 pt-0"
      />
      <tester-point-view v-if="room_id && selected_measurement_point_id" />
    </v-container>

    <!--evaluator and completed view-->
    <v-container
      class="pa-0"
      v-if="state === 'in_evaluation' || state === 'completed'"
    >
      <evaluator-rooms-view v-show="!room_id" class="pa-6 pt-0" />

      <evaluator-test-view
        v-if="room_id"
        class="pa-6"
        @data-submitting="setDataSubmitting"
      />
    </v-container>

    <!--rooms manager view -->
    <v-container
      class="pa-6 pt-7"
      v-if="
        !addingRoom &&
        !updatingRoom &&
        state !== 'in_testing' &&
        state !== 'in_evaluation' &&
        state !== 'completed'
      "
    >
      <v-list color="#1A1A1A" class="roomsList">
        <v-list-item-group style="max-height: 77vh" class="overflow-y-auto">
          <div v-for="room in selectedFloorRooms" :key="room.room_id">
            <v-list-item
              @click="() => state !== 'in_testing' && editRoom(room)"
            >
              <v-list-item-content class="pa-0">
                <v-container class="pa-0 ma-0 d-flex">
                  <v-list-item-title>
                    {{ room.name }}
                  </v-list-item-title>

                  <v-btn
                    icon
                    :hidden="state === 'in_testing'"
                    @click.stop="openModal(room)"
                  >
                    <v-icon style="max-height: 20px !important">
                      $iconDelete
                    </v-icon>
                  </v-btn>
                </v-container>
              </v-list-item-content>

              <v-list-item-action>
                <p v-if="room.illumination_tests && room.functional_tests">
                  {{
                    $tc("room.tests.label", totalTests(room), {
                      count: totalTests(room),
                    })
                  }}
                </p>
                <p v-else>0 TESTS</p>
              </v-list-item-action>
            </v-list-item>
            <v-divider></v-divider>
          </div>
        </v-list-item-group>
      </v-list>

      <v-container
        class="d-flex"
        :class="
          selectedFloorRooms.length > 0 ? 'justify-end' : 'justify-center'
        "
      >
        <niko-button
          :text="$t('room.add')"
          iconBefore="$iconAddZone"
          @onClick="
            setAddingRoom(true);
            setTab($t('room.tabs.info'));
          "
          primary
        >
        </niko-button>
      </v-container>
      <guidance-button page="configuration.rooms_list" />
    </v-container>

    <!--adding or updating room flow-->
    <v-container class="pa-0" v-if="addingRoom || updatingRoom">
      <v-row>
        <v-col>
          <v-tabs-items class="pa-6 pt-0" :value="tab">
            <v-tab-item>
              <room-general-info-tab />
            </v-tab-item>
            <v-tab-item>
              <template>
                <v-form v-if="closed">
                  <p class="mb-1 mt-3">
                    {{ $t("room.grid.deactivate.label") }}
                  </p>
                  <niko-button
                    class="mt-2 mb-4"
                    :text="$t('room.grid.deactivate.button')"
                    color="#1a1a1a"
                    @onClick="deactivateActivateClicked"
                  ></niko-button>

                  <p class="mb-3">{{ $t("room.grid.deactivate.comment") }}</p>

                  <niko-textarea
                    :rows="1"
                    :value="measurement_points_comment"
                    @onChange="(value) => setMeasurementPointsComment(value)"
                  />
                  <niko-slider
                    :min="0"
                    :max="1"
                    :label="$t('room.info.height')"
                    suffix="m"
                    step="0.05"
                    :accuracy="0.01"
                    :value="measurement_height"
                    :key="measurementHeightKey"
                    textFieldType="text"
                    @onChange="
                      (valueObj) => {
                        setMeasurementHeight(valueObj.value);
                        if (!valueObj.preventRerender) {
                          incrementKey('measurementHeightKey');
                        }
                      }
                    "
                  />
                  <niko-slider
                    :min="0"
                    :max="2"
                    :label="$t('room.info.distance')"
                    suffix="m"
                    step="0.05"
                    :accuracy="0.01"
                    :value="distance_from_wall"
                    :key="distanceFromWallKey"
                    textFieldType="text"
                    @onChange="
                      (valueObj) => {
                        manuallyChangeDistanceFromWall(valueObj.value);
                        if (!valueObj.preventRerender) {
                          incrementKey('distanceFromWallKey');
                        }
                      }
                    "
                  />
                  <niko-slider
                    :label="$t('room.grid.horizontally')"
                    :min="Number((max_distance_between_points / 2).toFixed(2))"
                    :max="max_distance_between_points"
                    suffix="m"
                    step="0.01"
                    :accuracy="0.01"
                    :value="Number(distance_between_points_x.toFixed(2))"
                    :key="distanceBetweenPointsXKey"
                    textFieldType="text"
                    @onChange="
                      (valueObj) => {
                        manuallyChangeDistanceBetweenPointsX(valueObj.value);
                        if (!valueObj.preventRerender) {
                          incrementKey('distanceBetweenPointsXKey');
                        }
                      }
                    "
                  />
                  <niko-slider
                    :label="$t('room.grid.vertically')"
                    :min="Number((max_distance_between_points / 2).toFixed(2))"
                    :max="max_distance_between_points"
                    suffix="m"
                    step="0.01"
                    :accuracy="0.01"
                    :value="Number(distance_between_points_y.toFixed(2))"
                    :key="distanceBetweenPointsYKey"
                    textFieldType="text"
                    @onChange="
                      (valueObj) => {
                        manuallyChangeDistanceBetweenPointsY(valueObj.value);
                        if (!valueObj.preventRerender) {
                          incrementKey('distanceBetweenPointsYKey');
                        }
                      }
                    "
                  />
                  <guidance-button
                    page="configuration.measurement_grid"
                    bottom="106"
                  />
                </v-form>
              </template>
            </v-tab-item>
            <v-tab-item>
              <room-tests-tab />
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
      <v-snackbar
        height="40px"
        min-height="40px"
        max-height="40px"
        :timeout="0"
        :value="!snackbarClosed"
        color="error"
        absolute
        top
        right
        class="mr-7"
      >
        {{ $t("room.error_message.main") }}

        <v-btn icon>
          <v-icon
            style="width: 16px !important; height: 16px"
            @click="snackbarClosed = true"
            >$iconClose
          </v-icon>
        </v-btn>
      </v-snackbar>
    </v-container>
    <niko-modal
      v-if="selectedRoom"
      :isVisible="isModalVisible"
      :title="$t('room.delete_modal.title', { room: selectedRoom.name })"
      :inputs="[]"
      :primaryButtonText="$t('room.delete_modal.delete')"
      :secondaryButtonText="$t('room.delete_modal.cancel')"
      @onSubmit="deleteRoomData"
      @onClose="closeModal"
    />

    <template
      v-slot:append
      v-if="state === 'in_configuration' && (addingRoom || updatingRoom)"
    >
      <div
        class="d-flex flex-row-reverse pa-6"
        style="border-top: 1px solid #4c4c4c"
      >
        <niko-button
          :text="addingRoom ? $t('room.save') : $t('room.update')"
          @onClick="
            () => {
              addingRoom ? addNewRoom() : updateRoomData();
            }
          "
          primary
        />
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import GuidanceButton from "@/components/guidance/GuidanceButton";
import NikoTextarea from "../UI/NikoTextarea";
import NikoButton from "../UI/NikoButton";
import NikoSlider from "../UI/NikoSlider";
import NikoModal from "../UI/NikoModal";
import SidebarToolbar from "./SidebarToolbar";
import RoomGeneralInfoTab from "./RoomGeneralInfoTab";
import { eventBus } from "@/components/EventBus";
import { pickBy } from "lodash";
import RoomTestsTab from "./RoomTestsTab";
import TesterRoomsView from "./TesterRoomsView";
import EvaluatorRoomsView from "./EvaluatorRoomsView";
import TesterTestView from "./TesterTestView";
import EvaluatorTestView from "./EvaluatorTestView";
import TesterPointView from "./TesterPointView";

export default {
  name: "right-sidebar",
  components: {
    NikoTextarea,
    TesterTestView,
    TesterRoomsView,
    EvaluatorRoomsView,
    TesterPointView,
    RoomTestsTab,
    RoomGeneralInfoTab,
    NikoButton,
    SidebarToolbar,
    NikoSlider,
    EvaluatorTestView,
    NikoModal,
    GuidanceButton,
  },
  data: () => ({
    selectedRoom: null,
    isModalVisible: false,
    snackbarClosed: true,
    dataSubmitting: false,
    closingForm: false,
    // Close your eyes for the next couple of fields (might save you a bit of headache)
    // These keys are used for forcing re-rendering of some slider components
    measurementHeightKey: "a1",
    distanceFromWallKey: "b1",
    distanceBetweenPointsXKey: "c1",
    distanceBetweenPointsYKey: "d1",
    // Open them again
  }),
  computed: {
    ...mapState("projectStore", ["buildings", "state", "project_id"]),
    ...mapState("floorStore", ["floor_id", "planURL"]),
    ...mapState("buildingStore", ["building_id"]),
    ...mapState("roomStore", [
      "tab",
      "name",
      "room_id",
      "room_type",
      "room_types",
      "room_type_id",
      "room_info_comment",
      "room_image_url",
      "longer_size_length",
      "corners",
      "isRoomValid",
      "addingRoom",
      "updatingRoom",
      "closed",
      "measurement_points_comment",
      "distance_between_points_x",
      "distance_between_points_y",
      "max_distance_between_points",
      "measurement_height",
      "distance_from_wall",
      "selected_measurement_point_id",
      "illumination_tests",
      "functional_tests",
      "selected_test_id",
    ]),
    drawerWidth() {
      return `${(100 * 4) / 12}%`;
    },
    selectedFloorRooms() {
      if (!this.addingRoom && this.building_id && this.floor_id) {
        const buildingIndex = this.buildings.findIndex(
          (building) => building.building_id === this.building_id
        );
        if (buildingIndex > -1) {
          const floorIndex = this.buildings[buildingIndex].floors.findIndex(
            (floor) => floor.floor_id === this.floor_id
          );
          return this.buildings[buildingIndex].floors[floorIndex].rooms || [];
        }
      }
      return [];
    },
    formCanBeClosed() {
      return !this.dataSubmitting && this.closingForm;
    },
  },
  methods: {
    ...mapActions("projectStore", [
      "addRoom",
      "updateRoom",
      "deleteRoom",
      "getProjectById",
    ]),
    ...mapActions("roomStore", ["uploadRoomFile"]),
    ...mapMutations("loaderStore", ["setIsLoading"]),
    ...mapMutations("roomStore", [
      "setTab",
      "setRoomId",
      "setRoomName",
      "setRoomType",
      "setRoomInfoComment",
      "setRoomImageUrl",
      "setRoomImageOptimizedUrl",
      "setRoomLength",
      "setAddingRoom",
      "setUpdatingRoom",
      "resetRoomState",
      "setMeasurementPointsComment",
      "setIsRoomClosed",
      "setRoomCorners",
      "setDistanceBetweenPointsX",
      "setDistanceBetweenPointsY",
      "setMeasurementHeight",
      "setDistanceFromWall",
      "setManuallyChangingDistanceBetweenPoints",
      "activateDeactivateAllPoints",
      "setFreezeDistanceBetweenPoints",
      "setMeasurementPoints",
      "setIlluminationTests",
      "setFunctionalTests",
      "setSelectedMeasurementPointId",
    ]),
    incrementKey(keyName) {
      const currentKey = this[`${keyName}`].split("");
      const currentKeyNumber = parseInt(currentKey[1]);
      const currentKeyLetter = currentKey[0];
      this[`${keyName}`] = (
        currentKeyLetter +
        (currentKeyNumber + 1)
      ).toString();
    },
    setDataSubmitting(newValue) {
      this.dataSubmitting = newValue;
    },
    async addNewRoom() {
      eventBus.$emit("validateRoomForm");
      if (!this.isRoomValid || !this.name) {
        return;
      }
      const room = {
        name: this.name,
        room_type: this.room_type,
        room_type_id: this.room_type_id,
        room_info_comment: this.room_info_comment,
        longer_size_length: this.longer_size_length,
        measurement_points_comment: this.measurement_points_comment,
        distance_between_points_x: this.distance_between_points_x,
        distance_between_points_y: this.distance_between_points_y,
        measurement_height: this.measurement_height,
        distance_from_wall: this.distance_from_wall,
      };
      if (this.planURL) {
        eventBus.$emit("centerRoom", async (dataURL) => {
          eventBus.$emit("resetValidation");
          await this.addRoom({
            room: pickBy(room, (prop) => prop !== undefined && prop !== ""),
            dataURL,
          });
        });
      } else {
        eventBus.$emit("resetValidation");
        await this.addRoom({
          room: pickBy(room, (prop) => prop !== undefined && prop !== ""),
        });
      }
    },
    async editRoom(room) {
      this.setIsLoading(true);
      const {
        room_id,
        name,
        room_type_id,
        room_info_comment,
        room_image_url,
        room_optimized_image_url,
        measurement_points_comment,
        longer_size_length,
        distance_between_points_x,
        distance_between_points_y,
        distance_from_wall,
        measurement_height,
        corners,
        measurement_points,
        illumination_tests,
        functional_tests,
      } = room;

      await this.setTab("Room info");
      await this.setUpdatingRoom(true);
      await this.setRoomId(room_id);
      await this.setRoomName(name);
      await this.setRoomType(room_type_id);
      await this.setRoomInfoComment(room_info_comment);
      await this.setRoomImageUrl(room_image_url);
      await this.setRoomImageOptimizedUrl(room_optimized_image_url);
      await this.setMeasurementPointsComment(measurement_points_comment);
      await this.setRoomLength(longer_size_length ?? 0);
      await this.setDistanceBetweenPointsX(distance_between_points_x);
      await this.setDistanceBetweenPointsY(distance_between_points_y);
      await this.setDistanceFromWall(distance_from_wall);
      await this.setMeasurementHeight(measurement_height);
      setTimeout(async () => {
        if (corners.length > 0) {
          await this.setFreezeDistanceBetweenPoints(true);
        }
        await this.setIsRoomClosed(corners.length > 0);
      }, 1);

      await this.setRoomCorners(
        corners.length > 0
          ? corners.map((corner) => ({
              ...corner,
            }))
          : []
      );

      const measurementPoints = measurement_points?.map((point) => {
        return {
          ...point,
        };
      });
      setTimeout(async () => {
        await this.setMeasurementPoints(measurementPoints ?? []);
      }, 1);

      const room_type = this.room_types.find(
        (type) => type.room_type_id === this.room_type_id
      );
      await this.setIlluminationTests(
        illumination_tests?.map((test) => ({
          ...test,
          required_ra: test.required_ra || room_type?.ra,
          required_rugl: test.required_rugl || room_type?.rugl,
        }))
      );

      await this.setFunctionalTests(
        functional_tests?.map((test) => ({
          ...test,
        }))
      );
      eventBus.$emit("centerRoom");
      this.setIsLoading(false);
    },
    async updateRoomData() {
      eventBus.$emit("validateRoomForm");
      if (!this.isRoomValid || !this.name) {
        return;
      }
      this.setIsLoading(true);
      const updatedRoomData = {
        room_id: this.room_id,
        name: this.name,
        room_type: this.room_type,
        room_type_id: this.room_type_id,
        room_info_comment: this.room_info_comment.trim(),
        measurement_points_comment: this.measurement_points_comment.trim(),
        longer_size_length: this.longer_size_length,
        distance_between_points_x: this.distance_between_points_x,
        distance_between_points_y: this.distance_between_points_y,
        measurement_height: this.measurement_height,
        distance_from_wall: this.distance_from_wall,
      };
      if (this.planURL) {
        eventBus.$emit("centerRoom", async (dataURL) => {
          if (dataURL) {
            const file = this.dataURLtoFile(dataURL, this.room_id);
            this.uploadRoomFile(file).then(({ url, optimizedUrl }) => {
              updatedRoomData.room_image_url = url;
              updatedRoomData.room_image_optimized_url = optimizedUrl;
              eventBus.$emit("resetValidation");
              this.updateRoom(
                pickBy(
                  updatedRoomData,
                  (prop) => prop !== undefined && prop !== ""
                )
              );
            });
          } else {
            eventBus.$emit("resetValidation");
            this.updateRoom(
              pickBy(
                updatedRoomData,
                (prop) => prop !== undefined && prop !== ""
              )
            );
          }
        });
      } else {
        eventBus.$emit("resetValidation");
        this.updateRoom(
          pickBy(updatedRoomData, (prop) => prop !== undefined && prop !== "")
        );
      }
    },
    dataURLtoFile(dataUrl, fileName) {
      let arr = dataUrl.split(","),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], fileName, { type: mime });
    },
    async deleteRoomData() {
      await this.deleteRoom(this.selectedRoom.room_id);
      this.isModalVisible = false;
    },
    openModal(room) {
      this.selectedRoom = room;
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
      this.selectedRoom = null;
    },
    clearData() {
      this.resetRoomState();
      eventBus.$emit("clearRoomData");
    },
    goBack() {
      if (this.state !== "in_configuration") {
        if (this.room_id) {
          this.selected_measurement_point_id
            ? this.setSelectedMeasurementPointId(null)
            : this.resetRoomState();
        } else {
          this.$router.push("/");
        }
      } else if (this.addingRoom || this.updatingRoom) {
        this.clearData();
      } else {
        this.$router.push("/");
      }
    },
    manuallyChangeDistanceBetweenPointsX(value) {
      const minValue = Number(
        (this.max_distance_between_points / 2).toFixed(2)
      );
      const adjustedValue =
        value < minValue
          ? minValue
          : value > this.max_distance_between_points
          ? this.max_distance_between_points
          : value;
      this.setDistanceBetweenPointsX(adjustedValue);
      this.setFreezeDistanceBetweenPoints(true);
    },
    manuallyChangeDistanceBetweenPointsY(value) {
      const minValue = Number(
        (this.max_distance_between_points / 2).toFixed(2)
      );
      const adjustedValue =
        value < minValue
          ? minValue
          : value > this.max_distance_between_points
          ? this.max_distance_between_points
          : value;
      this.setDistanceBetweenPointsY(adjustedValue);
      this.setFreezeDistanceBetweenPoints(true);
    },
    manuallyChangeDistanceFromWall(value) {
      this.setDistanceFromWall(value);
      this.setFreezeDistanceBetweenPoints(true);
    },
    deactivateActivateClicked() {
      this.activateDeactivateAllPoints();
    },
    totalTests(room) {
      return room.illumination_tests.length + room.functional_tests.length;
    },
  },
  watch: {
    isRoomValid(value) {
      this.snackbarClosed = value;
    },
    floor_id() {
      this.clearData();
      setTimeout(() => {
        this.$el.querySelector(".roomsList").scrollTop =
          this.$el.querySelector(".roomsList").scrollHeight;
      }, 500);
    },
    formCanBeClosed() {
      if (this.formCanBeClosed) {
        this.goBack();
        this.closingForm = false;
      }
    },
    room_id() {
      if (this.state === "in_testing" && !this.room_id) {
        this.getProjectById(this.project_id);
      }
    },
  },
  created() {
    eventBus.$on("tester-data-submitting", (newValue) => {
      this.dataSubmitting = newValue;
    });
  },
  destroyed() {
    eventBus.$off("tester-data-submitting");
  },
};
</script>

<style lang="scss">
.sidebar-tab {
  text-transform: none !important;
  font-weight: 400 !important;
}

.right-sidebar {
  .v-tab:not(.v-tab--active) {
    color: #e5e5e5 !important;
  }

  .theme--dark.v-tabs-items {
    background-color: #1a1a1a !important;
  }

  .v-navigation-drawer__content {
    &::-webkit-scrollbar {
      background-color: #202324;
      color: #aba499;
      width: 8px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #454a4d;
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: #575e62;
    }
    &::-webkit-scrollbar-thumb:active {
      background-color: #484e51;
    }
    &::-webkit-scrollbar-corner {
      background-color: #181a1b;
    }
  }
}
</style>
